import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [endOfList] - The end of list message element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {number} */
  #currentPage = 1;

  /** @type {boolean} Guards against overlapping infinite-scroll loads */
  #isLoadingNext = false;

  /** @type {number} Incremented when listing context changes so stale prefetch responses are ignored */
  #prefetchGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.paginationType === 'numbered') {
      return;
    }

    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);

    this.#storeImageRatioSettings();
    this.#initializePageState();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    
    this.#clickViewMore();
    this.#observeViewMore();
    this.#updateButtonVisibility();
  }

  /**
   * Initialize page state based on URL or reset to page 1
   */
  #initializePageState() {
    const url = new URL(window.location.href);
    const pageParam = url.searchParams.get('page');
    const storageKey = `paginated-list-${this.sectionId}-url`;
    const paginationActiveKey = `paginated-list-${this.sectionId}-active`;
    const currentUrlPath = url.pathname + url.search.replace(/[?&]page=\d+/, '');
    
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (pageNumber > 1 && this.paginationType !== 'numbered') {
        url.searchParams.delete('page');
        history.replaceState(null, '', url.toString());
        this.#currentPage = Math.max(1, this.#currentPage);
      }
      if (pageNumber >= 1) {
        this.#currentPage = pageNumber;
        return;
      }
      if (pageNumber >= 1) {
        this.#currentPage = pageNumber;
        return;
      }
    }
    
    this.#currentPage = 1;
    sessionStorage.setItem(storageKey, currentUrlPath);
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
  }

  /**
   * Facet/filter updates morph the section but reuse this element; prefetch caches must reset.
   */
  #onFilterUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.getAttribute('section-id') !== this.sectionId) return;

    this.#invalidatePrefetchedPages();
  };

  #invalidatePrefetchedPages() {
    if (this.paginationType === 'numbered') return;

    this.#prefetchGeneration++;

    this.pages.clear();
    

    this.#initializePageState();

    this.#resolveNextPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise?.();
    this.#resolvePreviousPagePromise = null;

    requestIdleCallback(() => {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    });

    queueMicrotask(() => this.#updateButtonVisibility());
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (this.paginationType !== 'infinite') return;

        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        const nextEntry = entries.find((entry) => entry.target === viewMoreNext && entry.isIntersecting);
        if (!nextEntry || this.#isLoadingNext) return;

        this.#isLoadingNext = true;
        try {
          await this.#renderNextPage();
        } finally {
          this.#isLoadingNext = false;
        }

        // Re-observe so a sentinel still in view after the append triggers another load
        this.infinityScrollObserver?.unobserve(viewMoreNext);
        this.infinityScrollObserver?.observe(viewMoreNext);
      },
      {
        rootMargin: '400px',
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  #clickViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext) return;

    viewMoreNext.addEventListener('click', async (event) => {
      event.preventDefault();

      const { grid } = this.refs;
      const cardCount = grid?.children.length ?? 0;
      const isKeyboard = event.detail === 0;

      await this.#renderNextPage();

      if (!isKeyboard) return;
      const firstNewLink = grid?.children[cardCount]?.querySelector('a');
      if (firstNewLink instanceof HTMLElement) firstNewLink.focus();
    })
    
    viewMorePrevious.addEventListener('click', () => {
      this.#renderPreviousPage();
    })
  }

  /**
   * Update the visibility of the load more button based on current page
   */
  #updateButtonVisibility() {
    const { viewMoreNext, grid, endOfList } = this.refs;
    if (!viewMoreNext || !grid) return;
    
    const lastPage = this.#getLastPage();

    const nextPage = this.#getPage('next');
    if (nextPage && viewMoreNext instanceof HTMLAnchorElement) viewMoreNext.href = nextPage.url.toString();
    
    const isLastPage = this.#currentPage >= lastPage;

    viewMoreNext.hidden = isLastPage;
    viewMoreNext.style.display = isLastPage ? 'none' : 'block';
    if (endOfList) {
      endOfList.style.display = isLastPage ? 'block' : 'none';
    }
  }

  /**
   * Get the current page number
   * @returns {number} The current page number
   */
  #getCurrentPage() {
    return this.#currentPage;
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page > this.#getLastPage() || pageInfo.page < 1) return false;

    return true;
  }

  #getLastPage() {
    const { grid } = this.refs;

    if (!grid) return 0;

    return Number(grid.getAttribute('last-page'));
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    const generationAtStart = this.#prefetchGeneration;

    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, page.url);

    if (generationAtStart !== this.#prefetchGeneration) return;

    this.pages.set(page.page, html);
    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    } else {
      this.#resolvePreviousPagePromise?.();
      this.#resolvePreviousPagePromise = null;
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#updateButtonVisibility();
      return;
    }

    const paginationActiveKey = `paginated-list-${this.sectionId}-active`;
    sessionStorage.setItem(paginationActiveKey, 'true');

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#processNewElements();
    this.#currentPage = nextPage.page;
    this.#updateButtonVisibility();

    // Infinite scroll loads automatically, so replace the entry to avoid a long Back-button stack
    const updateHistory = this.paginationType === 'infinite' ? history.replaceState : history.pushState;
    updateHistory.call(history, '', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    // Dispatch Swym event for new products
    document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    const paginationActiveKey = `paginated-list-${this.sectionId}-active`;
    sessionStorage.setItem(paginationActiveKey, 'true');

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#processNewElements();
    this.#currentPage = previousPage.page;
    this.#updateButtonVisibility();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });

    // Dispatch Swym event for new products
    document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? this.#currentPage - 1 : this.#currentPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Get the pagination type from the element attribute
   * @returns {string} The pagination type ('load_more' or 'numbered')
   */
  get paginationType() {
    return this.getAttribute('pagination-type') || 'load_more';
  }
}
